/**
 * @description Fetch doesnt support upload progress as axios does, since we're
 * migrating from axios to fetch, we need to implement our own upload progress until
 * fetch supports it. :(
 * You can pass this function to the fetchOptions.fetch property for
 * ApiService to use it as a fetch replacement.
 * @param onProgress - A function that is called with the progress of the upload.
 * @returns A function that can be used as a fetch replacement.
 * @example
 * const [progress, setProgress] = useState(0);
 * const { data } = await ApiService({
 *  ... // other options
 *  service: FileService,
 *  config: { fetch: withUploadProgress(setProgress) }, // <- pass the function
 * });
 */
export const withUploadProgress = (onProgress?: (progress: number) => void, signal?: AbortSignal) => {
    return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const request = new Request(input, init);

        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            const xhr = new XMLHttpRequest();

            if (onProgress) {
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const percentComplete = (event.loaded / event.total) * 100;
                        onProgress(percentComplete);
                    }
                });
            }

            xhr.addEventListener('load', () => {
                const response = new Response(xhr.response, {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    headers: new Headers({
                        'Content-Type': xhr.getResponseHeader('Content-Type') || 'application/json',
                    }),
                });
                resolve(response);
            });

            xhr.addEventListener('error', () => reject(new Error('Network error occurred')));
            xhr.addEventListener('timeout', () => reject(new Error('Request timed out')));

            if (signal) {
                signal.addEventListener('abort', () => {
                    xhr.abort();
                });
            }

            xhr.open(request.method || 'GET', request.url);

            if (request.headers) {
                request.headers.forEach((value, key) => {
                    if (value && key.toLowerCase() !== 'content-type') {
                        xhr.setRequestHeader(key, value);
                    }
                });
            }

            const formData = await request.formData();
            xhr.send(formData);
        });
    };
};
